import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import paymentLinkProvider from "src/providers/payment-link-provider";
import type { ListPaymentLinksParams, PaymentLinkParams } from "src/types";
import { sleep } from "src/utils";

const paymentLinkQueriers = {
	listPaymentLinks: (params?: ListPaymentLinksParams) => {
		const { merchantId } = useActiveMerchantId();

		return useQuery({
			queryKey: ["listPaymentLinks", merchantId, params],
			queryFn: async () => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return paymentLinkProvider.listPaymentLinks(merchantId, {
					...params,
					limit: params?.limit ?? 20,
					offset: params?.offset ?? 0,
				});
			},
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	createPaymentLink: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (params: PaymentLinkParams) => {
				if (!merchantId) {
					throw new Error("Missing merchantId");
				}
				return paymentLinkProvider.createPaymentLink(merchantId, params);
			},
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listPaymentLinks", merchantId],
				});
			},
		});
	},

	deletePaymentLink: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (paymentLinkId: string) => {
				if (!merchantId) {
					throw new Error("Missing merchantId");
				}
				return paymentLinkProvider.deletePaymentLink(merchantId, paymentLinkId);
			},
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listPaymentLinks", merchantId],
				});
			},
		});
	},
};

export default paymentLinkQueriers;
